// ABOUTME: Renders the destination flows of an expanded polling origin — the operator's own editable cards plus read-only peer branches
import { usePollingConfigs } from '../../hooks/useAdminQueries';
import { PollingItem, Config } from '../../api/adminApi';
import { INTEGRATION_COLORS } from './integrationColors';
import { byDestination, partitionConfigsByOperator } from './operatorFlows';

export function FlowDestinations({
  polling,
  operatorId,
  onEditConfig,
  onToggleConfig,
}: {
  polling: PollingItem;
  operatorId: string;
  onEditConfig: (config: Config) => void;
  onToggleConfig: (config: Config) => void;
}) {
  const { data, isLoading } = usePollingConfigs(polling.id);
  const { mine, others } = partitionConfigsByOperator(data?.configs || [], operatorId);

  if (isLoading) {
    return (
      <div className="flow-dest-loading">
        <span className="flow-dest-spinner" />
      </div>
    );
  }

  // The viewed operator's flows first, then any belonging to other operators
  // sharing this source — all branching from the one origin.
  const ordered = [
    ...[...mine].sort(byDestination),
    ...[...others].sort(byDestination),
  ];

  if (ordered.length === 0) {
    return <div className="flow-dest-empty">No destinations configured</div>;
  }

  // A single flow with no shared peers keeps the simple connector.
  if (ordered.length === 1) {
    const config = ordered[0];
    const configColor = INTEGRATION_COLORS[config.destination] || '#3b82f6';

    return (
      <div
        className="single-dest-connector"
        style={{ '--config-color': configColor } as React.CSSProperties}
      >
        <div className="single-dest-line"></div>
        <span className="single-dest-arrow" style={{ color: configColor }}>›</span>
        <div
          className={`single-dest-card clickable ${config.isDisabled ? 'disabled' : ''}`}
          data-testid={`config-${config.id}`}
          onClick={() => onEditConfig(config)}
        >
          <div className="flow-dest-left">
            <button
              className={`flow-toggle sm ${config.isDisabled ? 'off' : 'on'}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggleConfig(config);
              }}
            >
              <span className="flow-toggle-track">
                <span className="flow-toggle-thumb" />
              </span>
            </button>
            <span className="flow-dest-name" style={{ color: configColor }}>
              {config.destination}
            </span>
            <span className="flow-dest-entity">{config.entity}</span>
          </div>
          <div className="flow-dest-right">
            {config.destinationOperatorId && (
              <span
                className="flow-dest-operator-id"
                title={config.destinationOperatorId}
              >
                <span className="flow-dest-operator-label">Dest ID:</span>
                <span className="flow-dest-operator-value">
                  {config.destinationOperatorId}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Two or more flows branch out from the origin. Flows belonging to other
  // operators sharing this source render as greyed, read-only, labelled
  // branches: a disable stops them too, but they are not the viewed operator's
  // to change here.
  return (
    <>
      <div className="flow-connector-tree">
        <div className="tree-horizontal"></div>
      </div>

      <div className="flow-destinations">
        {ordered.map((config, idx) => {
          const total = ordered.length;
          const isFirst = idx === 0;
          const isLast = idx === total - 1;
          const isMine = config.techlogOperatorId === operatorId;
          const configColor = INTEGRATION_COLORS[config.destination] || '#3b82f6';

          const branch = (
            <div className={`tree-branch ${isFirst ? 'first' : isLast ? 'last' : 'middle'}`}>
              <svg className="branch-svg" viewBox="0 0 48 60" preserveAspectRatio="none">
                {isFirst ? (
                  <path d="M 0,0 L 0,60 M 0,0 L 48,0" className="branch-path" />
                ) : isLast ? (
                  <path d="M 0,0 L 0,60 M 0,60 L 48,60" className="branch-path" />
                ) : (
                  <path d="M 0,0 L 0,60 M 0,30 L 48,30" className="branch-path" />
                )}
              </svg>
              <span className="branch-arrow" style={isMine ? { color: configColor } : undefined}>›</span>
            </div>
          );

          if (!isMine) {
            const peerOperator = config.techlogOperatorId;
            return (
              <div
                key={config.id}
                className={`flow-dest readonly ${config.isDisabled ? 'disabled' : ''}`}
                data-testid={`config-other-${config.id}`}
              >
                {branch}
                <div className="flow-dest-card">
                  <div className="flow-dest-left">
                    <span className="flow-dest-name">{config.destination}</span>
                    <span className="flow-dest-entity">{config.entity}</span>
                    <span className="flow-dest-other-tag">Other operator</span>
                  </div>
                  <div className="flow-dest-right">
                    {peerOperator && (
                      <span className="flow-dest-operator-id" title={peerOperator}>
                        <span className="flow-dest-operator-label">Operator:</span>
                        <span className="flow-dest-operator-value">{peerOperator}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={config.id}
              className={`flow-dest ${config.isDisabled ? 'disabled' : ''}`}
              style={{
                '--config-color': configColor,
                '--delay': `${idx * 0.05}s`
              } as React.CSSProperties}
            >
              {branch}
              <div
                className="flow-dest-card clickable"
                data-testid={`config-${config.id}`}
                onClick={() => onEditConfig(config)}
              >
                <div className="flow-dest-left">
                  <button
                    className={`flow-toggle sm ${config.isDisabled ? 'off' : 'on'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleConfig(config);
                    }}
                  >
                    <span className="flow-toggle-track">
                      <span className="flow-toggle-thumb" />
                    </span>
                  </button>
                  <span className="flow-dest-name" style={{ color: configColor }}>
                    {config.destination}
                  </span>
                  <span className="flow-dest-entity">{config.entity}</span>
                </div>
                <div className="flow-dest-right">
                  {config.destinationOperatorId && (
                    <span
                      className="flow-dest-operator-id"
                      title={config.destinationOperatorId}
                    >
                      <span className="flow-dest-operator-label">Dest ID:</span>
                      <span className="flow-dest-operator-value">
                        {config.destinationOperatorId}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
